import React, { useContext, useState, useRef } from 'react';

import { Upload, notification } from 'antd';

import styled from 'styled-components';

import { GetTimestamp } from '@libs/NwMoment';
import { getBaseUrl } from '@libs/NwUtils';

import { UserContext } from '@contexts/UserContext';

import { NwUploadButton } from '@components/Gui/NwButton';

import NwIcon from './NwIcon';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Container = styled.div`
    .ant-upload-list {
        display: ${(props) => props.disable && 'none'};
    }
    .ant-upload {
        display: ${(props) => props.hide && 'none'};
    }
`;

const DescriptionList = styled.ul`
    padding-left: 20px;
`;

// The image pipeline can process these formats. The extension is checked too,
// because browsers often leave file.type empty for the less common ones.
const PROCESSABLE_IMAGE_EXTENSIONS = [
    'ani',
    'bmp',
    'cur',
    'exr',
    'gif',
    'ico',
    'jpeg',
    'jpg',
    'pbm',
    'png',
    'qoi',
    'tga',
    'tif',
    'tiff',
    'webp',
];

const PROCESSABLE_IMAGE_MIME_TYPES = [
    'image/bmp',
    'image/gif',
    'image/jpeg',
    'image/png',
    'image/tiff',
    'image/vnd.microsoft.icon',
    'image/webp',
    'image/x-exr',
    'image/x-icon',
    'image/x-ms-bmp',
    'image/x-portable-bitmap',
    'image/x-targa',
    'image/x-tga',
];

const getUploadFileExtension = (file) => {
    const fileName = `${file?.name || ''}`;
    const dotIndex = fileName.lastIndexOf('.');
    if (dotIndex < 0 || dotIndex === fileName.length - 1) return '';

    return fileName.slice(dotIndex + 1).toLowerCase();
};

const isProcessableImageFile = (file) => {
    if (PROCESSABLE_IMAGE_EXTENSIONS.includes(getUploadFileExtension(file))) return true;

    return PROCESSABLE_IMAGE_MIME_TYPES.includes(file?.type);
};

const NwUpload = ({
    onUploadStart,
    onShowInvalidFiles,
    onUploadEnd,
    onUploadError,
    dragDrop = false,
    endpoint,
    localUpload = false,
    maxSize,
    maxCount,
    multiple = true,
    types = [],
    showList = false,
    disable,
    hide,
    primary,
    label,
    acceptFolders = false,
    children,
}) => {
    const { currentUser } = useContext(UserContext);
    const authorization = currentUser ? { Authorization: 'Bearer ' + currentUser.Token } : {};
    const [generatedbatchid, setGeneratedbatchid] = useState(null);
    // const [batchcounter, setBatchcounter] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [fileList, setFileList] = useState([]);
    const [invalidFiles, setInvalidFiles] = useState([]);
    const uploadRef = useRef();

    const generateUploadToken = () => {
        if (localUpload) {
            return undefined;
        }
        if (!generatedbatchid) {
            setGeneratedbatchid('UPL_' + currentUser.UserID + '_' + GetTimestamp());
        }
        return getBaseUrl() + '/' + endpoint + '&batchID=' + generatedbatchid;
    };

    const checkIsValidSize = (file) => {
        if (!maxSize) return true;
        const sizeOk = file.size / 1024 / 1024 < maxSize;
        return sizeOk;
    };

    const getAcceptedMimeTypes = () => {
        const accept = [];
        let acceptall = false;
        types.forEach((el) => {
            if (el === 'image') {
                PROCESSABLE_IMAGE_EXTENSIONS.forEach((extension) => {
                    accept.push(`.${extension}`);
                });
                PROCESSABLE_IMAGE_MIME_TYPES.forEach((mimeType) => {
                    accept.push(mimeType);
                });
            }
            if (el === 'document') {
                accept.push('application/pdf');
            }
            if (el === 'data-document') {
                accept.push('text/csv');
                accept.push('application/vnd.ms-excel');
                accept.push('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
            }
            if (el === 'archive-document') {
                accept.push('application/zip');
                accept.push('application/x-rar-compressed');
            }
            if (el === 'office-document') {
                accept.push('application/msword');
                accept.push('application/vnd.openxmlformats-officedocument.wordprocessingml.document');
                accept.push('application/vnd.ms-powerpoint');
                accept.push('application/vnd.openxmlformats-officedocument.presentationml.presentation');
            }
            if (el === 'text-document') {
                accept.push('text/plain');
            }
            if (el === 'video') {
                accept.push('video/quicktime');
                accept.push('video/mp4');
                accept.push('video/m4v');
                accept.push('video/mkv');
            }
            if (el === 'all') {
                acceptall = true;
            }
        });
        if (acceptall) {
            return [];
        }
        return accept;
    };

    const checkIsValidFile = (file) => {
        if (localUpload && types.includes('image') && file.type && file.type.startsWith('image/')) {
            return true;
        }
        if (localUpload && types.includes('video') && file.type && file.type.startsWith('video/')) {
            return true;
        }

        if (types.includes('image') && isProcessableImageFile(file)) {
            return true;
        }

        const accept = getAcceptedMimeTypes();
        const acceptall = types.includes('all');
        const typeOk = acceptall || accept.includes(file.type);
        return typeOk;
    };

    const getUploadAccept = () => {
        if (types.includes('all')) return undefined;

        const accept = getAcceptedMimeTypes();
        if (localUpload && types.includes('image')) {
            accept.push('image/*');
        }
        if (localUpload && types.includes('video')) {
            accept.push('video/*');
        }
        return [...new Set(accept)].join(',');
    };

    const accept = getUploadAccept();

    const beforeUpload = (file, fileList) => {
        if (file.uid === fileList[fileList.length - 1].uid) {
            const invalidFiles = fileList
                .filter((file) => !checkIsValidSize(file) || !checkIsValidFile(file))
                .map((file) => {
                    return {
                        ...file,
                        name: file.name,
                        reason: !checkIsValidSize(file) ? 'is too big' : 'is not valid',
                    };
                });

            setInvalidFiles(invalidFiles);
            if (onShowInvalidFiles) {
                onShowInvalidFiles(invalidFiles);
            }
        }
        if (!checkIsValidSize(file)) {
            return false;
        }
        if (!checkIsValidFile(file)) {
            return false;
        }
        if (localUpload) {
            return false;
        }
        return true;
    };

    const getValidFileList = (uploadFileList) => {
        const validFileList = uploadFileList.filter((file) => checkIsValidSize(file) && checkIsValidFile(file));
        if (multiple === false || maxCount === 1) {
            return validFileList.slice(-1);
        }
        if (maxCount) {
            return validFileList.slice(-maxCount);
        }
        return validFileList;
    };

    const handleUploadChange = async (info) => {
        const fileList = getValidFileList(info.fileList); //[...info.fileList];

        setFileList(fileList);

        if (localUpload) {
            const localFile = info.file.originFileObj || info.file;
            if (!checkIsValidSize(localFile) || !checkIsValidFile(localFile)) {
                setUploading(false);
                return;
            }

            const localUploadInfo = {
                ...info,
                file: localFile,
                uploadFile: info.file,
                fileList,
            };

            try {
                setUploading(true);
                onUploadStart && (await onUploadStart(localUploadInfo));
                onUploadEnd && (await onUploadEnd(localUploadInfo));
            } catch (error) {
                console.error('Local Upload Error => ', error);
                onUploadError && onUploadError(error);
            } finally {
                setUploading(false);
            }
            return;
        }

        if (info.file.status === 'uploading') {
            onUploadStart && onUploadStart(info);
            setUploading(true);
            // setBatchcounter(fileList.length);
            return;
        }

        if (info.file.uid === info.fileList[info.fileList.length - 1].uid) {
            if (fileList.length === 0) {
                const args = {
                    message: `${info.fileList.length} file(s) not uploaded`,
                    description: (
                        <DescriptionList>
                            {info.fileList.map((file) => (
                                <li
                                    key={file.uid}
                                >{`${file.name} ${!checkIsValidSize(file) ? 'is too big' : 'is not valid'}`}</li>
                            ))}
                        </DescriptionList>
                    ),
                    duration: 0,
                    icon: <NwIcon icon={byPrefixAndName.fass['circle-xmark']} />,
                };
                notification.error(args);
                setGeneratedbatchid(null);
                // setBatchcounter(0);
                setUploading(false);
                setFileList([]);
                setInvalidFiles([]);
                onUploadEnd();
            }
        }

        if (info.file.status === 'done') {
            let checkIfAllFilesUploaded = true;
            for (var uploadingfile of fileList) {
                if (uploadingfile.status !== 'done') {
                    checkIfAllFilesUploaded = false;
                }
            }
            if (checkIfAllFilesUploaded) {
                //check if some errors occurs on the server (encoding or resizing)
                const filesWithError = fileList.filter(
                    (file) => file.response && file.response.length && file.response[0].error
                );
                if (filesWithError.length > 0) {
                    const args = {
                        message: `${filesWithError.length} file(s) not uploaded`,
                        description: (
                            <DescriptionList>
                                {filesWithError.map((file) => (
                                    <li key={file.uid}>
                                        <strong>{file.name}</strong>
                                        <br />
                                        <small>
                                            <em>{file.response[0].error}</em>
                                        </small>
                                    </li>
                                ))}
                            </DescriptionList>
                        ),
                        duration: 0,
                        style: { width: '34rem' },
                        icon: <NwIcon icon={byPrefixAndName.fass['circle-xmark']} />,
                    };
                    notification.error(args);
                }

                try {
                    setGeneratedbatchid(null);
                    // setBatchcounter(0);
                    setUploading(false);
                    setFileList([]);
                    setInvalidFiles([]);
                    onUploadEnd();
                } catch (error) {
                    console.error('Get Documents Error => ', error);
                    onUploadError();
                }
                if (invalidFiles.length > 0) {
                    const args = {
                        message: `${invalidFiles.length} file(s) not uploaded`,
                        description: (
                            <DescriptionList>
                                {invalidFiles.map((file) => (
                                    <li key={file.uid}>
                                        <strong>{file.name}</strong>
                                        <br />
                                        <small>
                                            <em>{file.reason}</em>
                                        </small>
                                    </li>
                                ))}
                            </DescriptionList>
                        ),
                        duration: 0,
                        style: { width: '34rem' },
                        icon: <NwIcon icon={byPrefixAndName.fass['circle-xmark']} />,
                    };
                    notification.error(args);
                }
            }
        }
    };

    return (
        <Container ref={uploadRef} disable={disable} hide={hide}>
            <Upload
                name="files"
                // 'drag' e' quello che antd usa dentro Upload.Dragger: senza, il drop non viene
                // intercettato e resta solo il click
                type={dragDrop ? 'drag' : 'select'}
                showUploadList={showList}
                listType="picture"
                action={localUpload ? undefined : generateUploadToken()}
                accept={accept}
                beforeUpload={(file, fileList) => beforeUpload(file, fileList)}
                onChange={handleUploadChange}
                multiple={multiple}
                maxCount={maxCount}
                defaultFileList={fileList}
                fileList={fileList}
                headers={authorization}
                directory={acceptFolders}
            >
                {children ? (
                    children
                ) : (
                    <NwUploadButton
                        disabled={disable}
                        label={label}
                        ghost={!primary}
                        primary={primary}
                        size="small"
                        loading={uploading}
                    />
                )}
            </Upload>
        </Container>
    );
};

export default NwUpload;
